import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import DashboardShell from "../components/shell/DashboardShell";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Trash2, Pencil, Globe2, Library, Share2, EyeOff, Download, Search, Building2, MapPin, X as XIcon, Calculator } from "lucide-react";
import { toast } from "sonner";
import USDateInput from "../components/USDateInput";
import AddressAutocomplete from "../components/AddressAutocomplete";
import LoopNetPullDialog from "../components/LoopNetPullDialog";
import CrexiPullDialog from "../components/CrexiPullDialog";
import CompGapDialog from "../components/CompGapDialog";

const PROP_TYPES = [
  { value: "office", label: "🏢 Office" },
  { value: "retail", label: "🛍️ Retail" },
  { value: "industrial", label: "🏭 Industrial" },
  { value: "warehouse", label: "📦 Warehouse" },
  { value: "flex", label: "🔧 Flex" },
  { value: "multifamily", label: "🏘️ Multifamily" },
  { value: "mixed_use", label: "🧬 Mixed-Use" },
  { value: "self_storage", label: "🔐 Self-Storage" },
  { value: "hospitality", label: "🏨 Hospitality" },
  { value: "medical", label: "🩺 Medical" },
  { value: "land", label: "🌾 Land" },
  { value: "other", label: "📦 Other" },
];

const SOURCE_OPTIONS = [
  { value: "manual", label: "Manual entry" },
  { value: "rentcast", label: "RentCast" },
  { value: "costar", label: "CoStar" },
  { value: "compstak", label: "CompStak" },
  { value: "crexi", label: "Crexi" },
  { value: "imported", label: "Imported (CSV/PDF)" },
];

const TYPE_LABELS = Object.fromEntries(PROP_TYPES.map((t) => [t.value, t.label]));

const EMPTY_FORM = {
  comp_type: "sale",
  property_type: "office",
  address: "",
  formatted_address: null,
  lat: null,
  lng: null,
  market: "",
  submarket: "",
  sqft: "",
  units: "",
  year_built: "",
  sale_price: "",
  sale_date: "",
  cap_rate: "",
  monthly_rent: "",
  lease_start_date: "",
  lease_term_months: "",
  source: "manual",
  broker: "",
  notes: "",
  share_to_marketplace: false,
};

const money = (n) => (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

export default function Comps() {
  const { user } = useAuth();
  const isPro = ["pro", "enterprise"].includes((user?.subscription_plan || "").toLowerCase()) || user?.role === "admin";
  const [tab, setTab] = useState("library"); // "library" | "marketplace"
  const [items, setItems] = useState([]);
  const [marketplaceItems, setMarketplaceItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [filters, setFilters] = useState({ comp_type: "", property_type: "", market: "" });
  const [geo, setGeo] = useState({ enabled: false, address: "", lat: null, lng: null, radius_miles: 5 });
  const [loopnetOpen, setLoopnetOpen] = useState(false);
  const [crexiOpen, setCrexiOpen] = useState(false);
  const [gapComp, setGapComp] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.comp_type) params.comp_type = filters.comp_type;
      if (filters.property_type) params.property_type = filters.property_type;
      if (filters.market) params.market = filters.market;
      if (geo.enabled && geo.lat != null && geo.lng != null && geo.radius_miles > 0) {
        params.center_lat = geo.lat;
        params.center_lng = geo.lng;
        params.radius_miles = geo.radius_miles;
      }
      const [r, s] = await Promise.all([
        api.get("/comps", { params }),
        api.get("/comps/stats/summary"),
      ]);
      setItems(r.data.items || []);
      setStats(s.data || null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load comps");
    } finally {
      setLoading(false);
    }
  };

  const loadMarketplace = async () => {
    try {
      const params = {};
      if (filters.comp_type) params.comp_type = filters.comp_type;
      if (filters.property_type) params.property_type = filters.property_type;
      if (filters.market) params.market = filters.market;
      if (geo.enabled && geo.lat != null && geo.lng != null && geo.radius_miles > 0) {
        params.center_lat = geo.lat;
        params.center_lng = geo.lng;
        params.radius_miles = geo.radius_miles;
      }
      const r = await api.get("/comps/marketplace", { params });
      setMarketplaceItems(r.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load marketplace");
    }
  };

  useEffect(() => {
    if (tab === "library") load();
    else loadMarketplace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, filters.comp_type, filters.property_type, filters.market, geo.enabled, geo.lat, geo.lng, geo.radius_miles]);

  const openNew = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({
      ...EMPTY_FORM,
      ...c,
      sqft: c.sqft ?? "",
      units: c.units ?? "",
      year_built: c.year_built ?? "",
      sale_price: c.sale_price ?? "",
      cap_rate: c.cap_rate ?? "",
      monthly_rent: c.monthly_rent ?? "",
      lease_term_months: c.lease_term_months ?? "",
      sale_date: c.sale_date || "",
      lease_start_date: c.lease_start_date || "",
      market: c.market || "",
      submarket: c.submarket || "",
      broker: c.broker || "",
      notes: c.notes || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
      const payload = {
        ...form,
        sqft: num(form.sqft),
        units: num(form.units),
        year_built: num(form.year_built),
        sale_price: num(form.sale_price),
        cap_rate: num(form.cap_rate),
        monthly_rent: num(form.monthly_rent),
        lease_term_months: num(form.lease_term_months),
        lat: num(form.lat),
        lng: num(form.lng),
      };
      if (!payload.address) {
        toast.error("Address is required");
        return;
      }
      if (editingId) {
        await api.put(`/comps/${editingId}`, payload);
        toast.success("Comp updated");
      } else {
        await api.post("/comps", payload);
        toast.success("Comp added");
      }
      setOpen(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this comp?")) return;
    try {
      await api.delete(`/comps/${id}`);
      toast.success("Comp deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const toggleShare = async (c) => {
    try {
      await api.put(`/comps/${c.id}`, { share_to_marketplace: !c.share_to_marketplace });
      toast.success(!c.share_to_marketplace ? "Shared to marketplace" : "Removed from marketplace");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update");
    }
  };

  const importFromMarketplace = async (mpId) => {
    try {
      await api.post(`/comps/${mpId}/import-from-marketplace`);
      toast.success("Imported into your library");
      // bounce the user over so they can edit it
      setTab("library");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Import failed");
    }
  };

  const showLease = form.comp_type === "lease";
  const showSale = form.comp_type === "sale";

  return (
    <DashboardShell>
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="flex items-start justify-between mb-6 gap-3 flex-wrap">
          <div>
            <div className="overline text-slate-500">Market intelligence</div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit" }}>
              CRE Comps
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Build a defensible book of sale & lease comps. Reuse them in valuations, share anonymized to the marketplace, or pull other landlords' data into your library.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="loopnet-pull-button"
              onClick={() => isPro ? setLoopnetOpen(true) : toast.info("LoopNet pull is included in Pro & Enterprise plans. Upgrade in Billing to unlock.")}
              className={`inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-sm border transition-colors ${isPro ? "bg-emerald-600 hover:bg-emerald-700 border-emerald-600 hover:border-emerald-700 text-white" : "bg-emerald-100 border-emerald-300 text-emerald-800 opacity-70"}`}
              title={isPro ? "Pull commercial listings from LoopNet (via RapidAPI)" : "Upgrade to Pro to enable"}
            >
              <Globe2 className="w-4 h-4" /> Pull from LoopNet
              {!isPro && <span className="ml-1 text-[9px] uppercase tracking-widest font-mono px-1 rounded-sm bg-violet-100 text-violet-700">Pro</span>}
            </button>
            <button
              data-testid="crexi-pull-button"
              onClick={() => isPro ? setCrexiOpen(true) : toast.info("Crexi pull is included in Pro & Enterprise plans. Upgrade in Billing to unlock.")}
              className={`inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-sm border transition-colors ${isPro ? "bg-emerald-600 hover:bg-emerald-700 border-emerald-600 hover:border-emerald-700 text-white" : "bg-emerald-100 border-emerald-300 text-emerald-800 opacity-70"}`}
              title={isPro ? "Pull commercial listings from Crexi (via Apify)" : "Upgrade to Pro to enable"}
            >
              <Building2 className="w-4 h-4" /> Pull from Crexi
              {!isPro && <span className="ml-1 text-[9px] uppercase tracking-widest font-mono px-1 rounded-sm bg-violet-100 text-violet-700">Pro</span>}
            </button>
            <button data-testid="add-comp-button" onClick={openNew} className="btn-primary-dark inline-flex items-center gap-2 text-sm">
              <Plus className="w-4 h-4" /> Add comp
            </button>
          </div>
        </div>

        {/* KPI strip — only on library tab */}
        {tab === "library" && stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="comp-stats">
            <Kpi label="Total comps" value={stats.total} bgClass="bg-green-50 border-green-200" />
            <Kpi label="Sale comps" value={stats.by_type?.sale ?? 0} bgClass="bg-purple-50 border-purple-200" />
            <Kpi label="Lease comps" value={stats.by_type?.lease ?? 0} bgClass="bg-green-50 border-green-200" />
            <Kpi label="Avg $/sqft" value={stats.avg_price_per_sqft != null ? `$${money(stats.avg_price_per_sqft)}` : "—"} sub={stats.avg_pps_sample ? `${stats.avg_pps_sample} sale comp${stats.avg_pps_sample === 1 ? "" : "s"}` : ""} bgClass="bg-amber-50 border-amber-200" />
          </div>
        )}

        {/* Tab switch + filters */}
        <div className="flex items-center gap-3 flex-wrap mb-4">
          <div className="inline-flex rounded-sm border border-slate-200 overflow-hidden bg-white">
            <button
              onClick={() => setTab("library")}
              data-testid="tab-comps-library"
              className={`px-4 py-2 text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1.5 ${tab === "library" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
            >
              <Library className="w-3.5 h-3.5" /> My library
            </button>
            <button
              onClick={() => setTab("marketplace")}
              data-testid="tab-comps-marketplace"
              className={`px-4 py-2 text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1.5 ${tab === "marketplace" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
            >
              <Globe2 className="w-3.5 h-3.5" /> Marketplace
            </button>
          </div>

          <div className="flex items-center gap-2 ml-auto flex-wrap">
            <select
              className="input-clean text-sm"
              data-testid="filter-comp-type"
              value={filters.comp_type}
              onChange={(e) => setFilters({ ...filters, comp_type: e.target.value })}
            >
              <option value="">All types</option>
              <option value="sale">Sale</option>
              <option value="lease">Lease</option>
            </select>
            <select
              className="input-clean text-sm"
              data-testid="filter-property-type"
              value={filters.property_type}
              onChange={(e) => setFilters({ ...filters, property_type: e.target.value })}
            >
              <option value="">All property types</option>
              {PROP_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                className="input-clean text-sm pl-8 w-44"
                placeholder="Market…"
                data-testid="filter-market"
                value={filters.market}
                onChange={(e) => setFilters({ ...filters, market: e.target.value })}
              />
            </div>
          </div>
        </div>

        {/* Radius search */}
        <RadiusSearchPanel geo={geo} setGeo={setGeo} />

        {/* Tables */}
        {tab === "library" ? (
          <LibraryTable
            items={items}
            loading={loading}
            geoActive={geo.enabled}
            onEdit={openEdit}
            onDelete={remove}
            onToggleShare={toggleShare}
            onValueGap={setGapComp}
          />
        ) : (
          <MarketplaceTable
            items={marketplaceItems}
            geoActive={geo.enabled}
            onImport={importFromMarketplace}
          />
        )}

        {/* Add/Edit dialog */}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="comp-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "Outfit" }}>
                {editingId ? "Edit comp" : "Add comp"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Comp type">
                  <select
                    className="input-clean w-full"
                    data-testid="comp-type-select"
                    value={form.comp_type}
                    onChange={(e) => setForm({ ...form, comp_type: e.target.value })}
                  >
                    <option value="sale">Sale</option>
                    <option value="lease">Lease</option>
                  </select>
                </Field>
                <Field label="Property type">
                  <select
                    className="input-clean w-full"
                    data-testid="comp-property-type-select"
                    value={form.property_type}
                    onChange={(e) => setForm({ ...form, property_type: e.target.value })}
                  >
                    {PROP_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </Field>
              </div>

              <Field label="Address">
                <AddressAutocomplete
                  value={form.address}
                  verified={!!form.lat && !!form.lng}
                  dataTestId="comp-address-input"
                  required
                  onChange={(v) => setForm((f) => ({ ...f, address: v }))}
                  onVerified={({ formatted_address, lat, lng }) =>
                    setForm((f) => ({ ...f, address: formatted_address, formatted_address, lat, lng }))
                  }
                  onReset={() => setForm((f) => ({ ...f, lat: null, lng: null, formatted_address: null }))}
                  placeholder="123 Tryon St, Charlotte, NC"
                />
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Market"><input className="input-clean w-full" data-testid="comp-market-input" placeholder="Charlotte MSA" value={form.market} onChange={(e) => setForm({ ...form, market: e.target.value })} /></Field>
                <Field label="Submarket"><input className="input-clean w-full" data-testid="comp-submarket-input" placeholder="Uptown" value={form.submarket} onChange={(e) => setForm({ ...form, submarket: e.target.value })} /></Field>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <Field label="Sqft"><input type="number" min="0" className="input-clean w-full" data-testid="comp-sqft-input" value={form.sqft} onChange={(e) => setForm({ ...form, sqft: e.target.value })} /></Field>
                <Field label="Units"><input type="number" min="0" className="input-clean w-full" data-testid="comp-units-input" value={form.units} onChange={(e) => setForm({ ...form, units: e.target.value })} /></Field>
                <Field label="Year built"><input type="number" min="1800" max="2100" className="input-clean w-full" data-testid="comp-year-input" value={form.year_built} onChange={(e) => setForm({ ...form, year_built: e.target.value })} /></Field>
              </div>

              {showSale && (
                <div className="grid grid-cols-3 gap-3 p-3 bg-emerald-50/40 border border-emerald-200 rounded-sm">
                  <Field label="Sale price ($)"><input type="number" min="0" step="1" className="input-clean w-full" data-testid="comp-sale-price-input" value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: e.target.value })} /></Field>
                  <Field label="Sale date"><USDateInput data-testid="comp-sale-date-input" value={form.sale_date} onChange={(iso) => setForm({ ...form, sale_date: iso })} /></Field>
                  <Field label="Cap rate (%)"><input type="number" min="0" step="0.01" className="input-clean w-full" data-testid="comp-cap-rate-input" value={form.cap_rate} onChange={(e) => setForm({ ...form, cap_rate: e.target.value })} /></Field>
                </div>
              )}

              {showLease && (
                <div className="grid grid-cols-3 gap-3 p-3 bg-sky-50/40 border border-sky-200 rounded-sm">
                  <Field label="Monthly rent ($)"><input type="number" min="0" step="1" className="input-clean w-full" data-testid="comp-monthly-rent-input" value={form.monthly_rent} onChange={(e) => setForm({ ...form, monthly_rent: e.target.value })} /></Field>
                  <Field label="Lease start"><USDateInput data-testid="comp-lease-start-input" value={form.lease_start_date} onChange={(iso) => setForm({ ...form, lease_start_date: iso })} /></Field>
                  <Field label="Term (months)"><input type="number" min="0" className="input-clean w-full" data-testid="comp-term-months-input" value={form.lease_term_months} onChange={(e) => setForm({ ...form, lease_term_months: e.target.value })} /></Field>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <Field label="Source">
                  <select className="input-clean w-full" data-testid="comp-source-select" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
                    {SOURCE_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </Field>
                <Field label="Broker"><input className="input-clean w-full" data-testid="comp-broker-input" placeholder="JLL / CBRE / etc." value={form.broker} onChange={(e) => setForm({ ...form, broker: e.target.value })} /></Field>
              </div>

              <Field label="Notes"><textarea rows={2} className="input-clean w-full" data-testid="comp-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>

              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.share_to_marketplace}
                  onChange={(e) => setForm({ ...form, share_to_marketplace: e.target.checked })}
                  data-testid="comp-share-checkbox"
                />
                <span>
                  <span className="font-semibold text-slate-900">Share anonymized to the marketplace</span>
                  <span className="block text-xs text-slate-500">Other landlords can see numbers + city/zip — never your owner identity or full street address.</span>
                </span>
              </label>

              <DialogFooter>
                <button type="button" onClick={() => setOpen(false)} className="btn-outline-dark">Cancel</button>
                <button type="submit" className="btn-primary-dark" data-testid="comp-submit-btn">{editingId ? "Save changes" : "Add comp"}</button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <LoopNetPullDialog
          open={loopnetOpen}
          onOpenChange={setLoopnetOpen}
          onImported={() => load()}
        />

        <CrexiPullDialog
          open={crexiOpen}
          onOpenChange={setCrexiOpen}
          onImported={() => load()}
        />

        <CompGapDialog
          open={!!gapComp}
          onOpenChange={(v) => !v && setGapComp(null)}
          comp={gapComp}
        />
      </div>
    </DashboardShell>
  );
}

function RadiusSearchPanel({ geo, setGeo }) {
  const apply = ({ formatted_address, lat, lng }) => {
    setGeo((g) => ({ ...g, address: formatted_address, lat, lng, enabled: true }));
  };
  const clear = () => setGeo({ enabled: false, address: "", lat: null, lng: null, radius_miles: 5 });

  return (
    <div
      className={`mb-4 border rounded-sm transition-colors ${geo.enabled ? "border-emerald-300 bg-emerald-50/40" : "border-slate-200 bg-white"}`}
      data-testid="radius-search-panel"
    >
      <div className="px-4 py-3 flex items-center gap-3 flex-wrap">
        <div className={`p-1.5 rounded-sm ${geo.enabled ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-500"}`}>
          <MapPin className="w-4 h-4" />
        </div>
        <div className="text-sm">
          <div className="font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Radius search</div>
          <div className="text-xs text-slate-500">Find comps within a mile-radius of any address. Sorted nearest-first.</div>
        </div>

        <div className="flex-1 min-w-[260px]">
          <AddressAutocomplete
            value={geo.address}
            verified={!!geo.lat && !!geo.lng}
            dataTestId="radius-address-input"
            onChange={(v) => setGeo((g) => ({ ...g, address: v }))}
            onVerified={apply}
            onReset={clear}
            placeholder="123 Main St, Charlotte, NC"
          />
        </div>

        <div className={`flex items-center gap-2 ${geo.enabled ? "" : "opacity-50 pointer-events-none"}`}>
          <input
            type="range"
            min="0.5"
            max="50"
            step="0.5"
            value={geo.radius_miles}
            onChange={(e) => setGeo((g) => ({ ...g, radius_miles: Number(e.target.value) }))}
            className="w-44 accent-emerald-700"
            data-testid="radius-slider"
            aria-label="Radius in miles"
          />
          <div className="w-20 text-right">
            <div className="font-mono font-bold text-slate-900" data-testid="radius-value">{geo.radius_miles} mi</div>
            <div className="text-[10px] uppercase tracking-widest font-mono text-slate-400">radius</div>
          </div>
        </div>

        {geo.enabled && (
          <button
            type="button"
            onClick={clear}
            className="ml-auto inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-slate-600 hover:text-red-600"
            data-testid="radius-clear-btn"
          >
            <XIcon className="w-3.5 h-3.5" /> Clear
          </button>
        )}
      </div>
    </div>
  );
}

function LibraryTable({ items, loading, geoActive, onEdit, onDelete, onToggleShare, onValueGap }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
      <table className="w-full text-sm" data-testid="comps-library-table">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr className="text-left">
            <Th>Type</Th>
            <Th>Property</Th>
            <Th>Address</Th>
            <Th>Market</Th>
            {geoActive && <Th className="text-right">Dist</Th>}
            <Th className="text-right">Sqft</Th>
            <Th className="text-right">Sale / Rent</Th>
            <Th className="text-right">$/sqft</Th>
            <Th className="text-right">Cap</Th>
            <Th>Date</Th>
            <Th className="text-right">Actions</Th>
          </tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan={geoActive ? 11 : 10} className="text-center py-12 text-slate-400">Loading…</td></tr>}
          {!loading && items.length === 0 && <tr><td colSpan={geoActive ? 11 : 10} className="text-center py-12 text-slate-400">No comps yet — add your first one to start building leverage.</td></tr>}
          {items.map((c) => (
            <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`comp-row-${c.id}`}>
              <Td>
                <span className={`overline px-1.5 py-0.5 rounded-sm border ${c.comp_type === "sale" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-sky-50 text-sky-800 border-sky-200"}`}>{c.comp_type}</span>
              </Td>
              <Td className="text-xs text-slate-600">{TYPE_LABELS[c.property_type] || c.property_type}</Td>
              <Td className="font-semibold text-slate-900 max-w-xs truncate" title={c.formatted_address || c.address}>{c.formatted_address || c.address}</Td>
              <Td className="text-xs text-slate-600">{c.market || "—"}{c.submarket ? <div className="text-[10px] text-slate-400">{c.submarket}</div> : null}</Td>
              {geoActive && (
                <Td className="text-right font-mono font-bold text-emerald-700">
                  {c.distance_miles != null ? `${c.distance_miles} mi` : "—"}
                </Td>
              )}
              <Td className="text-right font-mono">{c.sqft ? c.sqft.toLocaleString() : "—"}</Td>
              <Td className="text-right font-mono font-bold">
                {c.comp_type === "sale" ? (c.sale_price ? `$${money(c.sale_price)}` : "—") : (c.monthly_rent ? `$${money(c.monthly_rent)}/mo` : "—")}
              </Td>
              <Td className="text-right font-mono">{c.comp_type === "sale" ? (c.price_per_sqft ? `$${money(c.price_per_sqft)}` : "—") : (c.rent_per_sqft ? `$${money(c.rent_per_sqft)}` : "—")}</Td>
              <Td className="text-right font-mono">{c.cap_rate != null ? `${c.cap_rate.toFixed(2)}%` : "—"}</Td>
              <Td className="text-xs font-mono text-slate-500">{c.sale_date || c.lease_start_date || "—"}</Td>
              <Td className="text-right whitespace-nowrap">
                <button
                  onClick={() => onValueGap(c)}
                  className="text-slate-400 hover:text-emerald-700 p-1"
                  title="Value my portfolio with this comp"
                  data-testid={`comp-gap-${c.id}`}
                >
                  <Calculator className="w-4 h-4" />
                </button>
                <button
                  onClick={() => onToggleShare(c)}
                  className={`p-1 ${c.share_to_marketplace ? "text-emerald-600 hover:text-emerald-800" : "text-slate-400 hover:text-slate-700"}`}
                  title={c.share_to_marketplace ? "Shared to marketplace — click to unshare" : "Share to marketplace"}
                  data-testid={`comp-share-toggle-${c.id}`}
                >
                  {c.share_to_marketplace ? <Share2 className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
                <button onClick={() => onEdit(c)} className="text-slate-500 hover:text-slate-900 p-1" data-testid={`comp-edit-${c.id}`} title="Edit"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => onDelete(c.id)} className="text-slate-500 hover:text-red-600 p-1" data-testid={`comp-delete-${c.id}`} title="Delete"><Trash2 className="w-4 h-4" /></button>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MarketplaceTable({ items, geoActive, onImport }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="comps-marketplace-table">
      <div className="px-5 py-3 border-b border-slate-200 bg-amber-50/40 text-xs text-amber-900 flex items-center gap-2">
        <Globe2 className="w-3.5 h-3.5" />
        Anonymized comps shared by other landlords. Owner identity and exact street addresses are redacted; you see the city + ZIP.
      </div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr className="text-left">
            <Th>Type</Th>
            <Th>Property</Th>
            <Th>City / ZIP</Th>
            <Th>Market</Th>
            {geoActive && <Th className="text-right">Dist</Th>}
            <Th className="text-right">Sqft</Th>
            <Th className="text-right">Sale / Rent</Th>
            <Th className="text-right">$/sqft</Th>
            <Th className="text-right">Cap</Th>
            <Th>Date</Th>
            <Th className="text-right">Actions</Th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={geoActive ? 11 : 10} className="text-center py-12 text-slate-400">No marketplace comps {geoActive ? "within this radius" : "yet — share one of yours to seed the pool"}.</td></tr>}
          {items.map((c) => (
            <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`marketplace-row-${c.id}`}>
              <Td>
                <span className={`overline px-1.5 py-0.5 rounded-sm border ${c.comp_type === "sale" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-sky-50 text-sky-800 border-sky-200"}`}>{c.comp_type}</span>
              </Td>
              <Td className="text-xs text-slate-600">{TYPE_LABELS[c.property_type] || c.property_type}</Td>
              <Td className="font-semibold text-slate-900">{c.city_zip || "—"}</Td>
              <Td className="text-xs text-slate-600">{c.market || "—"}</Td>
              {geoActive && (
                <Td className="text-right font-mono font-bold text-emerald-700">
                  {c.distance_miles != null ? `${c.distance_miles} mi` : "—"}
                </Td>
              )}
              <Td className="text-right font-mono">{c.sqft ? c.sqft.toLocaleString() : "—"}</Td>
              <Td className="text-right font-mono font-bold">
                {c.comp_type === "sale" ? (c.sale_price ? `$${money(c.sale_price)}` : "—") : (c.monthly_rent ? `$${money(c.monthly_rent)}/mo` : "—")}
              </Td>
              <Td className="text-right font-mono">{c.comp_type === "sale" ? (c.price_per_sqft ? `$${money(c.price_per_sqft)}` : "—") : (c.rent_per_sqft ? `$${money(c.rent_per_sqft)}` : "—")}</Td>
              <Td className="text-right font-mono">{c.cap_rate != null ? `${c.cap_rate.toFixed(2)}%` : "—"}</Td>
              <Td className="text-xs font-mono text-slate-500">{c.sale_date || c.lease_start_date || "—"}</Td>
              <Td className="text-right">
                <button onClick={() => onImport(c.id)} className="btn-outline-dark text-xs inline-flex items-center gap-1.5" data-testid={`marketplace-import-${c.id}`} title="Copy this comp into your library">
                  <Download className="w-3.5 h-3.5" /> Import
                </button>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, className = "" }) { return <th className={`px-5 py-3 overline text-slate-600 ${className}`}>{children}</th>; }
function Td({ children, className = "", title }) { return <td title={title} className={`px-5 py-3 ${className}`}>{children}</td>; }
function Field({ label, children }) { return <label className="block"><div className="overline text-slate-600 mb-1">{label}</div>{children}</label>; }

function Kpi({ label, value, sub, bgClass = "bg-white border-slate-200" }) {
  return (
    <div className={`${bgClass} border rounded-sm p-4`}>
      <div className="overline text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-bold text-slate-900 font-mono">{value}</div>
      {sub && <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}
